import React from 'react';
import { User } from 'services/requests';
import { useUnlockIraUser } from 'hooks/api/iraUser';
import { useUnlockIndependentIraUser } from 'hooks/api/independentIra';
import { checkUser } from 'utils';
import { USER_TYPE } from 'constants/userTypes';
import { Modal, ModalProps } from 'components/Modal';
import Button from 'components/Button';
import messages from './StatusUserUnlockModal.messages';
import styles from './StatusUserUnlockModal.module.css';

type Props<T> = {
  user?: T;
  userType: string;
  onRequestClose: VoidFunction;
  isCancelAutoLock: boolean;
} & ModalProps;

const StatusUserUnlockModal = <T extends User>({
  onRequestClose,
  user,
  userType,
  isCancelAutoLock,
  ...restOfProps
}: Props<T>) => {
  const { mutateAsync: unlockIraUser, isLoading: isUnlockIraUserLoading } =
    useUnlockIraUser();
  const {
    mutateAsync: unlockIndependentIraUser,
    isLoading: isUnlockIndependentIraUserLoading,
  } = useUnlockIndependentIraUser();

  const isUnlockLoading = isUnlockIraUserLoading || isUnlockIndependentIraUserLoading;

  const onUnlockUserClick = async (): Promise<void> => {
    if (checkUser(user))
      switch (userType) {
        case USER_TYPE.iraUser:
          await unlockIraUser({ id: user.id.toString(), isCancelAutoLock });
          break;
        case USER_TYPE.independentIra:
          await unlockIndependentIraUser({ id: user.id.toString(), isCancelAutoLock });
          break;
      }
    onRequestClose();
  };

  const userName = `${user?.firstName} ${user?.middleName ?? ''} ${user?.lastName}`;

  return (
    <Modal
      {...restOfProps}
      onRequestClose={onRequestClose}
      className={styles.statusUserUnockModal}
    >
      <h3 className={styles.statusUserUnockModal__title}>{messages.title}</h3>
      <p className={styles.statusUserUnockModal__subTitle}>
        {messages.subTitleStart}
        {userName}
        {messages.subTitleEnd}
      </p>

      <div className={styles.statusUserUnockModal__buttons}>
        <Button variant={'outline'} onClick={onRequestClose}>
          {messages.buttonCancel}
        </Button>
        <Button onClick={onUnlockUserClick} isLoading={isUnlockLoading}>
          {messages.buttonSubmit}
        </Button>
      </div>
    </Modal>
  );
};

export default StatusUserUnlockModal;
